import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchPoliceStation } from "services/externalUserService";
import { addSchedule, editSchedule } from "services/prosecutorPoliceService";
import { PoliceStation, Schedule } from "types";
import { errorHandler, errorHandlerOnForm } from "utils/helper";
import { X } from "lucide-react";

type Props = {
  isOpen: boolean;
  action: "Add" | "Edit";
  schedule?: Schedule;
  onClose: (refetch: boolean) => void;
};

const defaultValue: Schedule = {
  id: 0,
  police_station_name: "",
  starting_date_time: new Date(),
  end_date_time: new Date(),
  org_phone_number: "",
  police_station_id: "",
  adminunit_id: "",
};

const ScheduleForm: FC<Props> = ({ isOpen, action, schedule, onClose }) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [formState, setFormState] = useState<Schedule>(
    schedule || defaultValue,
  );
  const [policeStations, setPoliceStations] = useState<PoliceStation[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (loginData) {
      setLoading(true);
      fetchPoliceStation()
        .then((c: any) => {
          const filteredStations =
            c?.results?.filter(
              (p: any) =>
                p?.is_police &&
                p?.organization?.admin_unit === loginData?.admin_unit,
            ) || [];
          setPoliceStations(filteredStations);

          // Auto-select first station if editing and no station selected
          if (
            action === "Add" &&
            filteredStations.length === 1 &&
            !formState.police_station_id
          ) {
            setFormState((prev) => ({
              ...prev,
              police_station_id: filteredStations[0].id.toString(),
            }));
          }
          setLoading(false);
        })
        .catch((error) => {
          showTooltip(
            error?.response?.data?.detail || errorHandler(error),
            "error",
          );
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginData]);

  useEffect(() => {
    if (schedule) {
      setFormState(schedule);
    }
  }, [schedule]);

  useEffect(() => {
    const police: any = policeStations?.find(
      (p) => p?.id?.toString() === formState?.police_station_id,
    );
    if (police) {
      setFormState((prev) => ({
        ...prev,
        org_phone_number: police?.organization?.org_phone_number,
        adminunit_id: police?.organization?.admin_unit,
        police_station_name: police?.organization?.name,
      }));
    }
  }, [formState?.police_station_id, policeStations]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors: string[] = [];

    if (!formState.police_station_id)
      errors.push(t("schedule.form.police_station_required"));

    if (!formState.starting_date_time) {
      errors.push(t("schedule.form.start_date_required"));
    } else if (
      formState.end_date_time &&
      new Date(formState.starting_date_time) > new Date(formState.end_date_time)
    ) {
      errors.push(t("schedule.form.start_after_end"));
    }

    if (!formState.end_date_time) {
      errors.push(t("schedule.form.end_date_required"));
    } else if (
      formState.starting_date_time &&
      new Date(formState.end_date_time) < new Date(formState.starting_date_time)
    ) {
      errors.push(t("schedule.form.end_before_start"));
    }

    return errors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const errors = validateForm();

    if (errors.length > 0) {
      showTooltip(errors.join(". "), "error");
      return;
    }

    setIsSubmitting(true);
    const submitAction = action === "Edit" ? editSchedule : addSchedule;

    try {
      await submitAction({
        ...formState,
      } as Schedule);

      showTooltip(
        action === "Edit"
          ? t("schedule.form.updated_successfully")
          : t("schedule.form.created_successfully"),
        "success",
      );
      setFormState(defaultValue);
      onClose(true);
    } catch (error: any) {
      showTooltip(
        error?.response?.data?.detail || errorHandlerOnForm(error),
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("modal-backdrop")) {
      onClose(false);
    }
  };

  const handleClose = () => {
    onClose(false);
  };

  if (!isOpen) return null;

  const today = new Date().toISOString().split("T")[0];

  return (
    <div
      className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 transition-all duration-300"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white rounded-t-xl px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {action === "Add"
                  ? t("schedule.form.create_title")
                  : t("schedule.form.edit_title")}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                {t("schedule.form.description")}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t("schedule.form.close_modal")}
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <p className="text-sm text-gray-500">
                  {t("schedule.form.loading_police_stations")}
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Police Station Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  {t("police_station")}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer hover:bg-gray-100"
                    name="police_station_id"
                    value={formState?.police_station_id || ""}
                    onChange={handleChange}
                    disabled={policeStations.length === 0}
                  >
                    <option value="">
                      {policeStations.length === 0
                        ? t("schedule.form.no_police_stations_available")
                        : t("schedule.form.select_police_station")}
                    </option>
                    {policeStations.map((station: any) => (
                      <option
                        key={station.organization?.uu_id}
                        value={station.organization?.uu_id}
                      >
                        {station?.organization?.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
                {policeStations.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {t("schedule.form.no_police_stations_in_admin_unit")}
                  </p>
                )}
              </div>

              {/* Date Selection Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Start Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    {t("start_date")}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all hover:bg-gray-100"
                      name="starting_date_time"
                      value={
                        formState?.starting_date_time
                          ? new Date(formState.starting_date_time)
                              .toISOString()
                              .split("T")[0]
                          : ""
                      }
                      min={today}
                      onChange={handleChange}
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* End Date */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    {t("end_date")}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all hover:bg-gray-100"
                      name="end_date_time"
                      value={
                        formState?.end_date_time
                          ? new Date(formState.end_date_time)
                              .toISOString()
                              .split("T")[0]
                          : ""
                      }
                      min={
                        formState.starting_date_time
                          ? new Date(formState.starting_date_time)
                              .toISOString()
                              .split("T")[0]
                          : today
                      }
                      onChange={handleChange}
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview Section */}
              {(formState.starting_date_time || formState.end_date_time) && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    {t("schedule.form.preview_title")}
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-blue-600">
                        {t("police_station")}
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {policeStations.find(
                          (p: any) =>
                            p.id.toString() === formState.police_station_id,
                        )?.organization?.name ||
                          t("schedule.form.not_selected")}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">
                        {t("schedule.form.duration")}
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {formState.starting_date_time && formState.end_date_time
                          ? `${new Date(
                              formState.starting_date_time,
                            ).toLocaleDateString()} - ${new Date(
                              formState.end_date_time,
                            ).toLocaleDateString()}`
                          : t("schedule.form.dates_not_set")}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="sticky bottom-0 pt-4 bg-white border-t border-gray-100 mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting || policeStations.length === 0}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                    isSubmitting || policeStations.length === 0
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.98]"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      {action === "Edit"
                        ? t("schedule.form.updating")
                        : t("schedule.form.creating")}
                    </>
                  ) : action === "Edit" ? (
                    t("schedule.form.edit_button")
                  ) : (
                    t("schedule.form.add_button")
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  {action === "Add"
                    ? t("schedule.form.add_note")
                    : t("schedule.form.edit_note")}
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleForm;
